import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RoutePaths from '../routes/RoutePaths';
import { Box, Container, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import ArticleList from './ArticleList';
import ArticleViewController from './ArticleViewController';
import useArticleRoute from './useArticleRoute';
import NewsQueueFilter from './NewsQueueFilter';
import DainTheme from '../DainTheme';
import {
    useGetConfigurations,
    useListNews,
    useListNewsDelta,
    useListNewsLocks,
} from '../api/NewsQueueApi';
import {
    ALL_SOURCE_CATEGORY_VALUES,
    ALL_SOURCE_VALUES,
    DEFAULT_SORT_DIRECTION,
    hasAvailableTargetMediaAlias,
    NEWS_NEW_HIGHLIGHT_FADE_MS,
    NEWS_DELTA_POLL_INTERVAL_MS,
    toSourceCategory,
    UI_PUBLISH_STATES,
    toUiPublishState,
} from './NewsQueueConstants';

export function useLocalStorageState(key, defaultValue) {
    const [state, setState] = useState(() => {
        try {
            const stored = localStorage.getItem(key);
            if (stored !== null) return JSON.parse(stored);
        } catch { /* ignore corrupt data */ }
        return typeof defaultValue === 'function' ? defaultValue() : defaultValue;
    });

    useEffect(() => {
        try {
            localStorage.setItem(key, JSON.stringify(state));
        } catch { /* ignore quota errors */ }
    }, [key, state]);

    return [state, setState];
}

export default function NewsQueue({ deskShortcut: initialDeskShortcut, shortcutMedia: initialShortcutMedia }) {
    const location = useLocation();
    const locksQuery = useListNewsLocks({ enabled: true, refetchInterval: 5000 });
    const configurationsQuery = useGetConfigurations({ enabled: true });
    const navigate = useNavigate();

    const { articleId, openArticle, closeArticle } = useArticleRoute();
    const [filterOpen, setFilterOpen] = useLocalStorageState('newsQueue.filterOpen', false);
    const [selectedTargetMedias, setSelectedTargetMedias] = useLocalStorageState('newsQueue.selectedTargetMedias', []);
    const [selectedMedia, setSelectedMedia] = useLocalStorageState('newsQueue.selectedMedia', null);
    const [shortcutMedia, setShortcutMedia] = useState(initialShortcutMedia || null);
    const [deskShortcut, setDeskShortcut] = useState(initialDeskShortcut || null);


    const [selectedCategories, setSelectedCategories] = useLocalStorageState('newsQueue.selectedCategories', []);
    const [selectedSources, setSelectedSources] = useLocalStorageState('newsQueue.selectedSources', []);
    const [selectedStatuses, setSelectedStatuses] = useLocalStorageState('newsQueue.selectedStatuses', [UI_PUBLISH_STATES.PENDING]);
    const [sortDirection, setSortDirection] = useLocalStorageState('newsQueue.sortDirection', DEFAULT_SORT_DIRECTION); // 'desc' | 'asc'
    const [deltaCursor, setDeltaCursor] = useState(() => new Date().toISOString());

    // Media filter for API calls - defined after all state
    // Priority: desk shortcut > shortcut media > selected media > selected target medias
    const apiMediaFilter = useMemo(() => {
        if (deskShortcut && deskShortcut.target_medias) {
            return deskShortcut.target_medias;
        }
        if (shortcutMedia) {
            return shortcutMedia;
        }
        return selectedMedia || selectedTargetMedias;
    }, [deskShortcut, shortcutMedia, selectedMedia, selectedTargetMedias]);
    
    const [deltaItemsById, setDeltaItemsById] = useState(() => new Map());
    const [newlyInsertedAtById, setNewlyInsertedAtById] = useState(() => new Map());

    const newsQuery = useListNews({ 
        parameters: { 
            show_processed: true,
            ...(apiMediaFilter.length > 0 ? { target_media: Array.isArray(apiMediaFilter) ? apiMediaFilter.join('+') : apiMediaFilter } : {})
        }, 
        enabled: true 
    });
    const newsDeltaQuery = useListNewsDelta({
        after: deltaCursor,
        parameters: { 
            show_processed: true,
            ...(apiMediaFilter.length > 0 ? { target_media: Array.isArray(apiMediaFilter) ? apiMediaFilter.join('+') : apiMediaFilter } : {})
        },
        enabled: Boolean(newsQuery.isSuccess),
        refetchInterval: NEWS_DELTA_POLL_INTERVAL_MS,
    });
    
    // Redirect to media selection if no media selected
    useEffect(() => {
        if (selectedMedia === null && location.pathname === '/') {
            navigate(RoutePaths.newsQueue.mediaSelection);
        }
    }, [selectedMedia, location.pathname, navigate]);

    // Shorthand routes render the queue in place, retaining their media filter.
    useEffect(() => {
        if (initialShortcutMedia && !initialDeskShortcut) {
            setSelectedTargetMedias(initialShortcutMedia);
        }
    }, [initialShortcutMedia, initialDeskShortcut, setSelectedTargetMedias]);

    // Handle shorthand media selection
    useEffect(() => {
        if (location.state?.shortcutMedia) {
            setShortcutMedia(location.state.shortcutMedia);
            // For shorthands, pre-select the media in the filter
            setSelectedTargetMedias(location.state.shortcutMedia);
        }
    }, [location.state]);

    // Handle desk shortcut selection
    useEffect(() => {
        if (location.state?.deskShortcut) {
            setDeskShortcut(location.state.deskShortcut);
            // For desk shortcuts, set the target medias from the shortcut
            if (location.state.shortcutMedia) {
                setShortcutMedia(location.state.shortcutMedia);
            }
        }
    }, [location.state]);

    const locksById = useMemo(() => {
        const raw = locksQuery?.data?.result;
        if (!Array.isArray(raw)) return new Map();

        const map = new Map();
        for (const lock of raw) {
            const newsId = String(lock?.news_id ?? '').trim();
            if (!newsId) continue;

            const expiresAt = Date.parse(String(lock?.lock_expires_at ?? ''));
            if (Number.isNaN(expiresAt) || expiresAt <= Date.now()) continue;

            map.set(newsId, lock);
        }
        return map;
    }, [locksQuery?.data?.result]);

    const items = useMemo(() => {
        const base = Array.isArray(newsQuery?.data?.result) ? newsQuery.data.result : [];
        const byId = new Map();

        for (const item of base) {
            const id = String(item?.id ?? '').trim();
            if (id) byId.set(id, item);
        }

        for (const [id, item] of deltaItemsById.entries()) {
            if (!id) continue;
            byId.set(id, item);
        }

        return Array.from(byId.values());
    }, [deltaItemsById, newsQuery?.data?.result]);





    React.useEffect(() => {
        const deltaItems = Array.isArray(newsDeltaQuery?.data?.result) ? newsDeltaQuery.data.result : [];
        if (deltaItems.length === 0) return;

        let maxBatchTs = Number.NaN;
        for (const item of deltaItems) {
            const createdTs = Date.parse(String(item?.timestamp_created ?? ''));
            if (Number.isNaN(createdTs)) continue;
            if (Number.isNaN(maxBatchTs) || createdTs > maxBatchTs) {
                maxBatchTs = createdTs;
            }
        }

        if (!Number.isNaN(maxBatchTs)) {
            const currentCursorTs = Date.parse(deltaCursor);
            if (Number.isNaN(currentCursorTs) || maxBatchTs > currentCursorTs) {
                setDeltaCursor(new Date(maxBatchTs).toISOString());
            }
        }

        setDeltaItemsById((prev) => {
            const next = new Map(prev);
            for (const item of deltaItems) {
                const id = String(item?.id ?? '').trim();
                if (!id) continue;
                next.set(id, item);
            }
            return next;
        });

        const now = Date.now();
        setNewlyInsertedAtById((prev) => {
            const next = new Map(prev);

            for (const item of deltaItems) {
                const id = String(item?.id ?? '').trim();
                if (!id) continue;
                if (next.has(id)) continue;
                next.set(id, now);
            }

            return next;
        });
    }, [deltaCursor, newsDeltaQuery?.data?.result]);

    React.useEffect(() => {
        if (newlyInsertedAtById.size === 0) return;

        const pruneTimer = setTimeout(() => {
            const now = Date.now();
            setNewlyInsertedAtById((prev) => {
                if (prev.size === 0) return prev;

                let changed = false;
                const next = new Map();

                for (const [id, insertedAt] of prev.entries()) {
                    if (now - insertedAt <= NEWS_NEW_HIGHLIGHT_FADE_MS) {
                        next.set(id, insertedAt);
                    } else {
                        changed = true;
                    }
                }

                return changed ? next : prev;
            });
        }, 1_000);

        return () => clearTimeout(pruneTimer);
    }, [newlyInsertedAtById]);

    const categories = useMemo(() => {
        const set = new Set();
        for (const item of items) {
            const category = toSourceCategory(item?.source_type);
            if (category) set.add(category);
        }
        return Array.from(new Set([...ALL_SOURCE_CATEGORY_VALUES, ...Array.from(set)]));
    }, [items]);

    const availableCategories = useMemo(() => {
        const set = new Set();
        for (const item of items) {
            const category = item?.category;
            if (category) set.add(category);
        }
        return Array.from(set);
    }, [items]);

    const sources = useMemo(() => {
        return [...ALL_SOURCE_VALUES];
    }, []);

    const availableSources = useMemo(() => {
        const set = new Set();
        const allowed = new Set(ALL_SOURCE_VALUES);
        for (const item of items) {
            const source = String(item?.source_type ?? '').trim();
            if (allowed.has(source)) set.add(source);
        }
        return Array.from(set);
    }, [items]);

    const targetMedias = useMemo(() => {
        const cfg = configurationsQuery?.data?.result;
        
        // Get media IDs from the new media_name_by_target_media structure
        const configured = cfg?.media_name_by_target_media && typeof cfg.media_name_by_target_media === 'object'
            ? Object.keys(cfg.media_name_by_target_media)
                .filter((x) => typeof x === 'string')
                .map((x) => x.trim())
                .filter((x) => x.length > 0)
            : [];

        const set = new Set();
        for (const item of items) {
            const v = item?.target_media;
            if (!v) continue;
            if (Array.isArray(v)) {
                for (const vv of v) {
                    const media = String(vv ?? '').trim();
                    if (media) set.add(media);
                }
            } else {
                const media = String(v ?? '').trim();
                if (media) set.add(media);
            }
        }
        
        // Remove canonicalization - use raw media IDs directly
        return Array.from(new Set([...configured, ...Array.from(set)]));
    }, [configurationsQuery?.data?.result, items]);

    const availableTargetMedias = useMemo(() => {
        const set = new Set();
        for (const item of items) {
            const v = item?.target_media;
            if (!v) continue;
            if (Array.isArray(v)) {
                for (const vv of v) {
                    const media = String(vv ?? '').trim();
                    if (media) set.add(media);
                }
            } else {
                const media = String(v ?? '').trim();
                if (media) set.add(media);
            }
        }

        // When using a desk shortcut, ensure all target medias from the shortcut are available
        if (deskShortcut && deskShortcut.target_medias) {
            deskShortcut.target_medias.forEach(media => {
                set.add(media);
            });
        }

        return targetMedias.filter((media) => hasAvailableTargetMediaAlias(set, media));
    }, [items, targetMedias, deskShortcut]);

    // Remove canonicalization - use raw media IDs directly
    const normalizedSelectedTargetMedias = useMemo(
        () => Array.from(new Set(selectedTargetMedias.filter(Boolean))),
        [selectedTargetMedias]
    );

    const activeMediaList = useMemo(() => {
        if (shortcutMedia) {
            return shortcutMedia;
        }
        if (deskShortcut && deskShortcut.target_medias) {
            return deskShortcut.target_medias;
        }
        return selectedMedia || targetMedias;
    }, [shortcutMedia, deskShortcut, selectedMedia, targetMedias]);

    const filteredTargetMedias = useMemo(() => {
        if (!activeMediaList) {
            return targetMedias;
        }
        return targetMedias.filter(media => activeMediaList.includes(media));
    }, [activeMediaList, targetMedias]);

    // When using a desk shortcut, we want to show all medias from the shortcut as available
    const deskTargetMedias = useMemo(() => {
        if (deskShortcut && deskShortcut.target_medias) {
            return deskShortcut.target_medias;
        }
        return [];
    }, [deskShortcut]);

    const filteredSelectedTargetMedias = useMemo(() => {
        if (!activeMediaList) {
            return normalizedSelectedTargetMedias;
        }
        return normalizedSelectedTargetMedias.filter(media => activeMediaList.includes(media));
    }, [activeMediaList, normalizedSelectedTargetMedias]);

    const handleSelectedTargetMediasChange = React.useCallback((next) => {
        setSelectedTargetMedias(Array.isArray(next) ? next : []);
    }, []);

    const availableStatuses = useMemo(() => {
        const set = new Set();
        for (const item of items) {
            const uiState = toUiPublishState(item?.publish_state);
            if (uiState) set.add(uiState);
        }
        return Array.from(set);
    }, [items]);

    const toggleCategoryPick = React.useCallback((category) => {
        if (!category) return;
        setSelectedCategories((prev) =>
            prev.includes(category) ? prev.filter((x) => x !== category) : [...prev, category]
        );
    }, []);

    const toggleStatusPick = React.useCallback((status) => {
        if (!status) return;
        setSelectedStatuses((prev) =>
            prev.includes(status) ? prev.filter((x) => x !== status) : [...prev, status]
        );
    }, []);

    return (
        <ThemeProvider theme={DainTheme.light}>
            <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: '0 0 auto' }}>
                    <Container maxWidth="md" sx={{ py: 0, px: 0 }}>
                        <NewsQueueFilter
                            filterOpen={filterOpen}
                            onToggleFilterOpen={() => setFilterOpen((v) => !v)}
                            sortDirection={sortDirection}
                            onToggleSortDirection={() =>
                                setSortDirection((v) => (v === 'desc' ? 'asc' : 'desc'))
                            }
                            targetMedias={deskShortcut ? deskShortcut.target_medias : filteredTargetMedias}
                            availableTargetMedias={deskShortcut ? deskShortcut.target_medias : availableTargetMedias}
                            selectedTargetMedias={normalizedSelectedTargetMedias}
                            onSelectedTargetMediasChange={handleSelectedTargetMediasChange}
                            categories={categories}
                            categoriesByGroup={configurationsQuery?.data?.result?.news_categories_by_group || {}}
                            availableCategories={availableCategories}
                            selectedCategories={selectedCategories}
                            onSelectedCategoriesChange={setSelectedCategories}
                            sources={sources}
                            availableSources={availableSources}
                            selectedSources={selectedSources}
                            onSelectedSourcesChange={setSelectedSources}
                            selectedStatuses={selectedStatuses}
                            availableStatuses={availableStatuses}
                            onSelectedStatusesChange={setSelectedStatuses}
                            configurations={configurationsQuery?.data?.result}
                        />
                    </Container>
                </Box>

                <Box sx={{ flex: '1 1 auto', overflowY: 'auto' }}>
                    <Container maxWidth="md" sx={{ py: 0 }}>
                        <Box sx={{ px: 0, pt: 0 }}>
                            {newsQuery.isLoading && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ my: 2, textAlign: 'left' }}
                                >
                                    Ladataan…
                                </Typography>
                            )}
                            {newsQuery.isError && (
                                <Typography variant="body2" color="error" sx={{ my: 2, textAlign: 'left' }}>
                                    Uutisten lataus epäonnistui.
                                </Typography>
                            )}
                            <ArticleList
                                onOpen={openArticle}
                                headerOffset={0}
                                selectedTargetMedias={filteredSelectedTargetMedias}
                                selectedCategories={selectedCategories}
                                selectedSources={selectedSources}
                                selectedStatuses={selectedStatuses}
                                onCategoryPick={toggleCategoryPick}
                                onStatusPick={toggleStatusPick}
                                items={items}
                                locksById={locksById}
                                newlyInsertedAtById={newlyInsertedAtById}
                                sortDirection={sortDirection}
                                configurations={configurationsQuery?.data?.result}
                            />
                        </Box>
                    </Container>
                </Box>
                <ArticleViewController articleId={articleId} onClose={closeArticle}
                    locksById={locksById} items={items} deskShortcut={deskShortcut} />
            </Box>
        </ThemeProvider>
    );
}
